/**
 * Constantes y generador de bytes para comandos estándar ESC/POS.
 * Compatible con impresoras térmicas Epson, Star, Xprinter, Rongta, POS-58, POS-80, Bixolon, etc.
 */

export const ESC = 0x1b;
export const FS = 0x1c;
export const GS = 0x1d;
export const DLE = 0x10;
export const EOT = 0x04;

export const CMD = {
  // Inicialización
  INIT: new Uint8Array([ESC, 0x40]), // ESC @

  // Alineación
  ALIGN_LEFT: new Uint8Array([ESC, 0x61, 0x00]), // ESC a 0
  ALIGN_CENTER: new Uint8Array([ESC, 0x61, 0x01]), // ESC a 1
  ALIGN_RIGHT: new Uint8Array([ESC, 0x61, 0x02]), // ESC a 2

  // Énfasis / Negrita
  BOLD_ON: new Uint8Array([ESC, 0x45, 0x01]), // ESC E 1
  BOLD_OFF: new Uint8Array([ESC, 0x45, 0x00]), // ESC E 0

  // Subrayado
  UNDERLINE_ON: new Uint8Array([ESC, 0x2d, 0x01]), // ESC - 1
  UNDERLINE_OFF: new Uint8Array([ESC, 0x2d, 0x00]), // ESC - 0

  // Tamaño de texto
  SIZE_NORMAL: new Uint8Array([GS, 0x21, 0x00]), // GS ! 0
  SIZE_DOUBLE_HEIGHT: new Uint8Array([GS, 0x21, 0x01]),
  SIZE_DOUBLE_WIDTH: new Uint8Array([GS, 0x21, 0x10]),
  SIZE_DOUBLE: new Uint8Array([GS, 0x21, 0x11]), // 2x ancho y 2x alto

  // Inversión (blanco sobre negro)
  INVERT_ON: new Uint8Array([GS, 0x42, 0x01]), // GS B 1
  INVERT_OFF: new Uint8Array([GS, 0x42, 0x00]), // GS B 0

  // Tabla de caracteres CP850 (Multilingüe con acentos y español)
  CODEPAGE_CP850: new Uint8Array([ESC, 0x74, 0x02]), // ESC t 2
  CODEPAGE_CP437: new Uint8Array([ESC, 0x74, 0x00]), // ESC t 0

  // Apertura de cajón de dinero (RJ11 / RJ12)
  DRAWER_PIN2: new Uint8Array([ESC, 0x70, 0x00, 0x19, 0xfa]), // ESC p 0 25 250
  DRAWER_PIN5: new Uint8Array([ESC, 0x70, 0x01, 0x19, 0xfa]), // ESC p 1 25 250

  // Corte de papel
  CUT_FULL: new Uint8Array([GS, 0x56, 0x00]), // GS V 0
  CUT_PARTIAL: new Uint8Array([GS, 0x56, 0x01]), // GS V 1
  CUT_FEED_3: new Uint8Array([GS, 0x56, 0x42, 0x03]), // GS V 'B' 3 (avanza 3 líneas y corta)

  // Salto de línea
  LF: new Uint8Array([0x0a]),

  // Beep / Zumbador
  BEEP: new Uint8Array([ESC, 0x42, 0x02, 0x02]),
};

/**
 * Tabla de traducción de caracteres UTF-8 a Codepage 850 para impresoras térmicas.
 * Asegura que acentos y caracteres en español (á, é, í, ó, ú, ñ, Ñ, ¿, ¡, $) se impriman correctos.
 */
const CP850_MAP: Record<string, number> = {
  "á": 0xa0,
  "é": 0x82,
  "í": 0xa1,
  "ó": 0xa2,
  "ú": 0xa3,
  "Á": 0xb5,
  "É": 0x90,
  "Í": 0xd6,
  "Ó": 0xe0,
  "Ú": 0xe9,
  "ñ": 0xa4,
  "Ñ": 0xa5,
  "¿": 0xa8,
  "¡": 0xad,
  "ü": 0x81,
  "Ü": 0x9a,
  "°": 0xf8,
  "º": 0xa7,
  "ª": 0xa6,
};

/** Convierte una cadena de texto a un Uint8Array codificado en Codepage 850 */
export function encodeCp850(text: string): Uint8Array {
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (CP850_MAP[char] !== undefined) {
      bytes.push(CP850_MAP[char]);
    } else {
      const code = char.charCodeAt(0);
      if (code < 128) {
        bytes.push(code);
      } else {
        // Caracter no mapeado, normalizar a ASCII equivalente o espacio
        const normalized = char.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const normCode = normalized.charCodeAt(0);
        bytes.push(normCode < 128 ? normCode : 0x20);
      }
    }
  }
  return new Uint8Array(bytes);
}

/** Concatena múltiples Uint8Array en uno solo */
export function concatBytes(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((acc, curr) => acc + curr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
}
